import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, useMediaQuery} from '@salesforce/retail-react-app/app/components/shared/ui';

const MegaMenuOverlay = ({isOpen, onClose, onForceClose}) => {
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isMouseEnterEnabled, setIsMouseEnterEnabled] = useState(false);
  const [isDesktop] = useMediaQuery('(min-width: 992px)');
  const headerHeightRef = useRef(0);

  // Get header height dynamically
  const updateHeaderHeight = () => {
    const headerWrapper = document.querySelector('[data-header-wrapper]');

    if (headerWrapper) {
      const height = headerWrapper.getBoundingClientRect().height;

      headerHeightRef.current = height;
      setHeaderHeight(height);
    }
  };

  // Set up resize listener once on mount, recalculate header height on resize
  useEffect(() => {
    // Initial measurement
    updateHeaderHeight();

    // Update on resize
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []); // Empty deps - only run once on mount

  // Delay enabling mouse enter handler to prevent immediate close on keyboard open
  useEffect(() => {
    if (!isOpen) {
      setIsMouseEnterEnabled(false);

      return;
    }

    // Enable mouse enter after a short delay (allows keyboard-initiated opens to work)
    const timeoutId = setTimeout(() => {
      setIsMouseEnterEnabled(true);
    }, 150);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  // On desktop, overlay starts below header. On mobile, full screen overlay.
  const overlayTop = isDesktop && headerHeight > 0 ? `${headerHeight}px` : 0;

  // Handle mouse enter - only close if enabled (after delay)
  const handleMouseEnter = () => {
    if (isMouseEnterEnabled) {
      onClose();
    }
  };

  return (
    <Box
      backgroundColor="rgba(0, 0, 0, 0.2)"
      bottom={0}
      left={0}
      pointerEvents="auto"
      position="fixed"
      right={0}
      top={overlayTop}
      zIndex={999}
      sx={{
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onForceClose || onClose}
      onMouseEnter={handleMouseEnter}
    />
  );
};

MegaMenuOverlay.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onForceClose: PropTypes.func,
};

export {MegaMenuOverlay};
